import PropTypes from 'prop-types';
import React from 'react';

import { displayValue, popupFieldValue } from '../utils/popupContent';

/**
 * The body of a clicked feature's popup: each field from the layer's `popup`
 * spec (map document v3), with its humanized label.
 *
 * The layer name and the feature's title are not repeated here: BiliMap puts
 * them in the info window header (utils/popupContent.js, buildPopupHeader).
 * Values are untrusted and rendered only as JSX text (displayValue() for
 * labels, popupFieldValue() for values, which also shows "No data" for a
 * missing value and trims long decimals).
 * Renders nothing when the layer has no popup spec or no fields to show.
 */
export default function LayerPopup({ layer, feature }) {
    const properties = feature?.properties || {};
    const fields = layer.popup?.fields || [];
    if (fields.length === 0) {
        return null;
    }

    return (
        <div className="bili-map__popup small">
            <dl className="bili-map__properties mb-0">
                {fields.map((field) => (
                    <React.Fragment key={field.key}>
                        {/* text-break: a long unbroken value (a URL, JSON)
                            wraps instead of widening the info window past a
                            phone screen. */}
                        <dt className="text-break">
                            {displayValue(field.label)}
                        </dt>
                        <dd className="text-break">
                            {popupFieldValue(properties[field.key])}
                        </dd>
                    </React.Fragment>
                ))}
            </dl>
        </div>
    );
}

LayerPopup.propTypes = {
    /** A layer from normalizeMapDocument(): its popup spec (or null) */
    layer: PropTypes.shape({
        popup: PropTypes.shape({
            // Always a string for v3; null for a TRANSITIONAL(v1) layer,
            // which has no title field (mapDocument.js v1Popup()).
            title_field: PropTypes.string,
            fields: PropTypes.arrayOf(
                PropTypes.shape({
                    key: PropTypes.string.isRequired,
                    label: PropTypes.string.isRequired,
                })
            ).isRequired,
        }),
    }).isRequired,
    /** The clicked GeoJSON feature */
    feature: PropTypes.shape({
        properties: PropTypes.object,
    }),
};
